/**
 * Content Packs API
 *
 * Content pack management endpoints for themes, backgrounds, etc.
 * Corresponds to backend /v1/admin/content-packs/*
 */

import apiClient from './client';

// ============================================================================
// Typed Metadata Interfaces
// ============================================================================

export interface ViewPort {
  width: number;
  height: number;
}

export interface TextBox {
  width: number;
  height: number;
  top: number;
  left: number;
}

export interface ItemDescription {
  theme?: string;
  emotion?: string;
  keywords?: string[];
}

export interface ItemRenderMetadata {
  view_port?: ViewPort;
  text_box?: TextBox;
  font_color?: string;
  outline_color?: string;
  rotation_angle?: number;
  font_scale_x?: number;
  line_spacing?: number;
  text_align?: 'left' | 'center' | 'right';
  shadow_blur?: number;
  shadow_opacity?: number;
  display_name?: string;
  description?: ItemDescription;
}

export interface OverlayConfig {
  enabled: boolean;
  filename?: string;
  position?: string;
}

export interface PackMetadata {
  overlay?: OverlayConfig;
  default_metadata?: ItemRenderMetadata;
}

// ============================================================================
// Content Pack Types
// ============================================================================

export interface ContentPack {
  id: string;
  type: string;
  name: string;
  display_name: string;
  description: string;
  metadata: PackMetadata;
  status: 'draft' | 'active' | 'archived';
  is_default: boolean;
  sort_order: number;
  item_count: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  items?: ContentPackItem[];
}

export interface ContentPackItem {
  id: string;
  filename: string;
  object_name: string;
  item_type: string;
  metadata: ItemRenderMetadata;
  file_size: number;
  content_type: string;
  sort_order: number;
  created_at?: string;
  thumbnail_url?: string;
}

export interface ContentPackListResponse {
  packs: ContentPack[];
  total: number;
}

export interface ContentPackCreate {
  type: string;
  name: string;
  display_name: string;
  description?: string;
  metadata?: Record<string, any>;
  status?: string;
}

export interface ContentPackUpdate {
  display_name?: string;
  description?: string;
  metadata?: PackMetadata;
  status?: string;
  sort_order?: number;
  is_default?: boolean;
}

export const contentPacksApi = {
  /**
   * List content packs with optional type/status filters
   */
  list: async (type?: string, status?: string): Promise<ContentPackListResponse> => {
    const params: Record<string, string> = {};
    if (type) params.type = type;
    if (status) params.status = status;
    const response = await apiClient.get<ContentPackListResponse>('/content-packs', { params });
    return response.data;
  },

  /**
   * Get a single content pack by ID (includes items)
   */
  get: async (packId: string): Promise<ContentPack> => {
    const response = await apiClient.get<ContentPack>(`/content-packs/${packId}`);
    return response.data;
  },

  /**
   * Create a new content pack
   */
  create: async (data: ContentPackCreate): Promise<ContentPack> => {
    const response = await apiClient.post<ContentPack>('/content-packs', data);
    return response.data;
  },

  /**
   * Update an existing content pack
   */
  update: async (packId: string, data: ContentPackUpdate): Promise<ContentPack> => {
    const response = await apiClient.put<ContentPack>(`/content-packs/${packId}`, data);
    return response.data;
  },

  /**
   * Delete a content pack
   */
  delete: async (packId: string): Promise<void> => {
    await apiClient.delete(`/content-packs/${packId}`);
  },

  /**
   * Upload items (files) to a content pack
   */
  uploadItems: async (packId: string, files: File[], itemType = 'background'): Promise<ContentPackItem[]> => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    const response = await apiClient.post<ContentPackItem[]>(
      `/content-packs/${packId}/items?item_type=${itemType}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Update item metadata
   */
  updateItemMetadata: async (packId: string, itemId: string, metadata: ItemRenderMetadata): Promise<ContentPackItem> => {
    const response = await apiClient.put<ContentPackItem>(
      `/content-packs/${packId}/items/${itemId}`,
      { metadata }
    );
    return response.data;
  },

  /**
   * Delete a single item from a content pack
   */
  deleteItem: async (packId: string, itemId: string): Promise<void> => {
    await apiClient.delete(`/content-packs/${packId}/items/${itemId}`);
  },

  /**
   * Reorder items within a content pack
   */
  reorderItems: async (packId: string, itemOrders: Array<{ id: string; sort_order: number }>): Promise<void> => {
    await apiClient.put(`/content-packs/${packId}/items/reorder`, { item_orders: itemOrders });
  },

  /**
   * One-time migration from filesystem to OSS
   */
  migrateFilesystem: async (): Promise<any> => {
    const response = await apiClient.post('/content-packs/migrate-filesystem');
    return response.data;
  },
};
